(() => {
  const heroSelector = '[data-nh-hero]';

  class NettHierHero {
    constructor(root) {
      if (root.dataset.nhInitialized === 'true') return;

      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-nh-hero-slide]'));
      this.previousButton = root.querySelector('[data-nh-hero-prev]');
      this.nextButton = root.querySelector('[data-nh-hero-next]');
      this.toggleButton = root.querySelector('[data-nh-hero-toggle]');
      this.tabs = Array.from(root.querySelectorAll('[data-nh-hero-tab]'));
      this.label = root.querySelector('[data-nh-hero-label]');
      this.count = root.querySelector('[data-nh-hero-count]');
      this.pauseIcon = root.querySelector('[data-nh-hero-pause-icon]');
      this.playIcon = root.querySelector('[data-nh-hero-play-icon]');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(5000, Number.parseInt(root.dataset.interval, 10) || 7000);
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.userPaused = root.dataset.autoplay !== 'true' || this.motionPreference.matches;
      this.isHovering = false;
      this.hasFocus = false;
      this.timer = null;
      this.handleVisibility = () => this.schedule();
      this.handleMotionChange = (event) => {
        if (event.matches) this.userPaused = true;
        this.syncToggle();
        this.schedule();
      };

      if (this.slides.length < 2) {
        root.querySelector('[data-nh-hero-controls]')?.setAttribute('hidden', '');
        return;
      }

      root.dataset.nhInitialized = 'true';
      root.nhHeroInstance = this;
      this.bindEvents();
      this.show(this.index);
      this.syncToggle();
      this.preload(this.index + 1);
      this.schedule();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.select(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.select(this.index + 1));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(Number.parseInt(tab.dataset.index, 10)));
      });
      this.toggleButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncToggle();
        this.schedule();
      });

      this.root.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.clearTimer();
      });
      this.root.addEventListener('mouseleave', () => {
        this.isHovering = false;
        this.schedule();
      });
      this.root.addEventListener('focusin', () => {
        this.hasFocus = true;
        this.clearTimer();
      });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.hasFocus = false;
          this.schedule();
        }
      });
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.motionPreference.addEventListener?.('change', this.handleMotionChange);
    }

    select(index) {
      this.show(index);
      this.schedule();
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === this.index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
      });

      const activeSlide = this.slides[this.index];
      if (this.label) this.label.textContent = activeSlide.dataset.label || '';
      if (this.count) this.count.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.preload(this.index + 1);
    }

    preload(index) {
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      const image = slide?.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';
    }

    syncToggle() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Automatischen Bildwechsel starten' : 'Automatischen Bildwechsel pausieren'
      );
      if (this.pauseIcon) this.pauseIcon.hidden = this.userPaused;
      if (this.playIcon) this.playIcon.hidden = !this.userPaused;
    }

    schedule() {
      this.clearTimer();
      if (this.userPaused || this.isHovering || this.hasFocus || document.hidden) return;

      this.timer = window.setTimeout(() => {
        this.show(this.index + 1);
        this.schedule();
      }, this.interval);
    }

    clearTimer() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    destroy() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.motionPreference.removeEventListener?.('change', this.handleMotionChange);
      delete this.root.nhHeroInstance;
      delete this.root.dataset.nhInitialized;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(heroSelector).forEach((hero) => new NettHierHero(hero));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(heroSelector).forEach((hero) => hero.nhHeroInstance?.destroy());
  });
})();
